
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';

import Layout from '../../../components/Layout';
import { Namespace } from '../../../enums/namespaces';
import styles from './index.module.scss';
import { PlayBase } from '../../../typings/api';
import { getSharedApiClient } from '../../../api/client';
import React, { useCallback, useRef } from 'react';
import { useFade } from '../../../hooks';
import {
  basicSEO,
  createFormSchema,
  createRedirectIfNeeded,
  emailRule,
  getNoTrialTranslationKeyIfNeeded,
  notEmptyRule,
  Permalink,
  resetForm,
  setLoadingState,
  validateForm,
} from '../../../helpers';
import storageHelper from '../../../helpers/storageHelper';
import { StorageKeys } from '../../../enums/storageKeys';
import { useRouter } from 'next/router';
import AppLink from '../../../components/AppLink/AppLink';
import { redirectWhenAuth, useAuth } from '../../../context/authContext';
import { GetServerSidePropsContext, GetStaticPropsResult } from 'next';
import { StaticPathParams } from '../../../typings/staticPaths';
import Head from 'next/head';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import Select from 'components/Select/Select';
import { useLang } from 'context/langContext';
import { Country } from 'typings/api/country';
import { useState } from 'react';
import { FeaturedSectionType } from 'enums/FeaturedSectionType';
interface Props {
  plays: PlayBase[];
  countries: Country[];
  countryCode: string;
}
const RegisterStep1 = ({ plays, countries, countryCode }: Props) => {
  const { t } = useTranslation(Namespace.Register);
  const { locale } = useLang();
  const router = useRouter();
  const { user } = useAuth();

  const ref = useRef(null);
  const formRef = useRef(null);
  const serverError = useRef(null);

  const [countryData] = useState(
    countries.map((c) => ({
      value: c.id,
      label: c.title,
    })),
  );
  const preselectedCountry = countries.find((c) => c.suggested)?.id;

  useFade(ref, plays);

  const onSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const EMAIL_RULE = t('common:form.email_format');
      const NOT_EMPTY_RULE = t('common:form.not_empty');
      const form = event.target as HTMLFormElement;
      const email = form.email.value;
      const fullName = form.fullName.value;
      const taxCountryId = form.taxCountryId.value;
      const validateObject = {
        email,
        fullName,
        taxCountryId,
      };
      const formSchema = createFormSchema({
        email: emailRule(EMAIL_RULE, NOT_EMPTY_RULE),
        fullName: notEmptyRule(NOT_EMPTY_RULE),
        taxCountryId: notEmptyRule(NOT_EMPTY_RULE),
      });
      const valid = validateForm(formSchema, validateObject, formRef.current);
      if (valid) {
        setLoadingState(formRef.current);
        const emailExists = await getSharedApiClient().checkUserExists(email);
        if (!emailExists.exists) {
          storageHelper.saveJSON(StorageKeys.REGISTER_DETAILS, {
            email,
            fullName,
            taxCountryId,
          });
          router.push(Permalink.registerStep2(locale));
        } else {
          resetForm(formRef.current);
          serverError.current.innerHTML = t('common:form.already_exists');
          formRef.current.classList.add('display-server-errors');
        }
      } else {
        resetForm(formRef.current);
      }
    },
    [formRef],
  );

  return (
    <Layout countryCode={countryCode}>
      <Head>{basicSEO(t('seo:register_step1_title'), t('seo:register_step1_description'))}</Head>
      <main className={styles.register}>
        <div className={styles.registerWrapper}>
          <div ref={ref} className={['fade-container', styles.fadeContainer].join(' ')}>
            <ul className="swiper-wrapper">
              {plays?.map((play) => (
                <li key={play.id} className="swiper-slide fade-slide">
                  <div>
                    <img src={play.poster_url} alt={play.title} />
                  </div>
                  <p>
                    {play.title}
                    {play.theatre?.name && (
                      <>
                        {' '}
                        / <span>{play.theatre.name}</span>
                      </>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          <h1>{t('title')}</h1>
          <Trans
            i18nKey={getNoTrialTranslationKeyIfNeeded('register:description', countryCode)}
            components={[<p className={styles.description} />, <em />]}
          />
          <form ref={formRef} onSubmit={onSubmit}>
            <label>
              {t('email')}
              <input type="text" id="email" name="email" />
              <span ref={serverError} className="server-error"></span>
            </label>
            <label>
              {t('full_name')}
              <input type="text" id="fullName" name="fullName" />
              <span></span>
            </label>
            <label>
              {t('register:country_of_residence')}
              <Select
                defaultValue={preselectedCountry}
                name="taxCountryId"
                options={countryData}
                defaultLabel={t('common:pick_country')}
              />
            </label>
            <button type="submit" className="btn btn-primary">
              {t('btn_continue')}
            </button>
            <Trans
              i18nKey={'register:register_terms'}
              components={[
                <p className={styles.registerTerms} />,
                <AppLink href={Permalink.termsAndConditions(locale)}>
                  <a />
                </AppLink>,
                <AppLink href={Permalink.privacyPolicy(locale)}>
                  <a />
                </AppLink>,
              ]}
            />
          </form>
          <p>
            {t('already_account')}{' '}
            <AppLink href={Permalink.login(locale)}>
              <a>{t('login_prompt')}</a>
            </AppLink>
          </p>
        </div>
      </main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetStaticPropsResult<Props>> {
  const { apiClient, locale, countryCode } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  const countries = await apiClient.getCountries();
  const { sections } = await apiClient.getHomepageSections();
  const { plays } = sections.find((s) => s.type === FeaturedSectionType.BANNER);

  return {
    props: { plays, countries, countryCode },
  };
}

export default redirectWhenAuth(RegisterStep1);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/register/step-1/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  